import productDefinitionsJson from "./C1_ProductDefinitions.json";
import eloItemCatalogJson from "./C1_EloItemCatalog.json";
import type { C1_Language } from "../C1_language";
import { c1_state } from "../model/C1_runtimeContext";

export type C1_ButtonProductId = keyof typeof productDefinitionsJson.buttons;
export type C1_ButtonUiTemplate = "simple" | "touch" | "panel";
export type C1_LightProductId = keyof typeof eloItemCatalogJson.lightTypes;
export type C1_LightUsageCategory = keyof typeof eloItemCatalogJson.lightUsageCategories;

export interface C1_ButtonProductDefinition {
  description: Record<C1_Language, string>;
  buttonRows: number;
  uiTemplate: C1_ButtonUiTemplate;
  width: number;
  height: number;
}

export interface C1_LightProductDefinition {
  descriptionShort: Record<C1_Language, string>;
  description: Record<C1_Language, string>;
  icon: { name: string };
  usage: C1_LightUsageCategory[];
}

export interface C1_ProductDefinitions {
  buttons: Record<C1_ButtonProductId, C1_ButtonProductDefinition>;
}

const productDefinitions = productDefinitionsJson as C1_ProductDefinitions;
const lightProductDefinitions = eloItemCatalogJson.lightTypes as Record<C1_LightProductId, C1_LightProductDefinition>;
const lightAssetUrls = import.meta.glob("./lights/*.{png,svg}", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

export function getButtonProductDefinition(
  id: string | undefined,
): C1_ButtonProductDefinition | undefined {
  if (!id || !Object.prototype.hasOwnProperty.call(productDefinitions.buttons, id)) return undefined;
  return productDefinitions.buttons[id as C1_ButtonProductId];
}

export function getButtonProductIds(): C1_ButtonProductId[] {
  return Object.keys(productDefinitions.buttons) as C1_ButtonProductId[];
}

export function getButtonProductDescription(id: string | undefined): string {
  return getButtonProductDefinition(id)?.description[c1_state.language] ?? id ?? "";
}

export function getLightProductDefinition(
  id: string | undefined,
): C1_LightProductDefinition | undefined {
  if (!id || !Object.prototype.hasOwnProperty.call(lightProductDefinitions, id)) return undefined;
  return lightProductDefinitions[id as C1_LightProductId];
}

export function getLightProductIds(): C1_LightProductId[] {
  return Object.keys(lightProductDefinitions) as C1_LightProductId[];
}

export function getLightProductIdsForUsage(usage: C1_LightUsageCategory | undefined): C1_LightProductId[] {
  if (!usage) return getLightProductIds();
  return getLightProductIds().filter((id) => lightProductDefinitions[id].usage.includes(usage));
}

export function getLightProductDescription(id: string | undefined): string {
  return getLightProductDefinition(id)?.description[c1_state.language] ?? id ?? "";
}

/** Visible symbol radius plus a small gap, divided by its SVG viewBox size. */
export function getLightSymbolOutlineRadiusRatio(id: string | undefined): number | undefined {
  switch (id) {
    case "symbol-light":
    case "symbol-light-ceiling":
    case "symbol-light-wall": return 54 / 322.5;
    default: return undefined;
  }
}

export function getLightProductImageUrl(id: string | undefined): string | undefined {
  const iconName = getLightProductDefinition(id)?.icon.name;
  return iconName ? lightAssetUrls[`./lights/${iconName}`] : undefined;
}

export default productDefinitions;
